import React, { useEffect, useState, useCallback } from "react";
import { Modal, Spinner, Button } from "react-bootstrap";
import GridReportService from "../../../_services/GridReportService";
import { DetailColumn, DetailResponse, ActiveFilter } from "../types";

interface DetailModalProps {
  show: boolean;
  onHide: () => void;
  reportType: string;
  xDimension: string;
  yDimension: string;
  xValue: number | string;
  yValue: number | string;
  xLabel: string;
  yLabel: string;
  startDate?: string;
  endDate?: string;
  filters?: ActiveFilter[];
  legendIndex?: number;
}

const PAGE_SIZE = 25;

const DetailModal: React.FC<DetailModalProps> = ({
  show,
  onHide,
  reportType,
  xDimension,
  yDimension,
  xValue,
  yValue,
  xLabel,
  yLabel,
  startDate,
  endDate,
  filters,
  legendIndex,
}) => {
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState<DetailColumn[]>([]);
  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      await GridReportService.exportDetail({
        reportType,
        xDimension,
        yDimension,
        xValue,
        yValue,
        xLabel,
        yLabel,
        startDate,
        endDate,
        filters,
        legendIndex,
      });
    } catch {
      // surfaced by the axios wrapper
    } finally {
      setExporting(false);
    }
  }, [
    reportType, xDimension, yDimension, xValue, yValue, xLabel, yLabel,
    startDate, endDate, filters, legendIndex,
  ]);

  const fetchDetail = useCallback(
    async (pageNum: number) => {
      setLoading(true);
      setError(null);
      try {
        const resp: DetailResponse = await GridReportService.getDetail({
          reportType,
          xDimension,
          yDimension,
          xValue,
          yValue,
          startDate,
          endDate,
          filters,
          page: pageNum,
          pageSize: PAGE_SIZE,
          legendIndex,
        });
        setColumns(resp.columns);
        setRows(resp.rows);
        setTotal(resp.total);
        setPage(pageNum);
      } catch (err: any) {
        setError(
          err.response?.data?.error || err.message || "Failed to load details"
        );
      } finally {
        setLoading(false);
      }
    },
    [reportType, xDimension, yDimension, xValue, yValue, startDate, endDate, filters, legendIndex]
  );

  useEffect(() => {
    if (show) {
      fetchDetail(0);
    }
  }, [show, fetchDetail]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const startRow = page * PAGE_SIZE + 1;
  const endRow = Math.min((page + 1) * PAGE_SIZE, total);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      dialogClassName="grid-report-detail-modal"
    >
      <Modal.Header className="gr-detail-header">
        <div className="gr-detail-header-main">
          <span className="gr-detail-header-icon">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="8" y1="13" x2="16" y2="13" />
              <line x1="8" y1="17" x2="16" y2="17" />
            </svg>
          </span>
          <div>
            <div className="gr-detail-title">
              {yLabel} <span className="gr-detail-title-sep">/</span> {xLabel}
            </div>
            <div className="gr-detail-subtitle">
              {total} record{total !== 1 ? "s" : ""}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || loading || total === 0}
            className="gr-detail-export"
            title="Export all records to Excel"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {exporting ? "Exporting…" : "Export to Excel"}
          </button>
          <button
            type="button"
            onClick={onHide}
            aria-label="Close"
            className="gr-detail-close"
          >
            &times;
          </button>
        </div>
      </Modal.Header>
      <Modal.Body className="gr-detail-body">
        {loading && (
          <div className="gr-detail-state">
            <Spinner role="status" animation="border" size="sm" style={{ color: "var(--brand-primary)", marginRight: 8 }} />
            Loading records…
          </div>
        )}

        {error && <div className="gr-detail-error">{error}</div>}

        {!loading && !error && rows.length === 0 && (
          <div className="gr-detail-state">No records found.</div>
        )}

        {!loading && !error && rows.length > 0 && (
          <table className="gr-detail-table">
            <thead>
              <tr>
                <th className="gr-detail-th-num">#</th>
                {columns.map((col) => (
                  <th key={col.key}>{col.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx}>
                  <td className="gr-detail-td-num">
                    {page * PAGE_SIZE + idx + 1}
                  </td>
                  {columns.map((col) => (
                    <td key={col.key}>{row[col.key] ?? "-"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal.Body>

      {totalPages > 1 && (
        <Modal.Footer className="gr-detail-footer">
          <span className="gr-detail-footer-info">
            Showing {startRow}–{endRow} of {total}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant="secondary"
              size="sm"
              disabled={page === 0}
              onClick={() => fetchDetail(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => fetchDetail(page + 1)}
            >
              Next
            </Button>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default DetailModal;
